import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { Notification } from '../types';

const NotificationBadge = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [dismissingAll, setDismissingAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError(error as Error);
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  const markAsProcessed = async (notificationId: string) => {
    if (processing === notificationId) return;

    setProcessing(notificationId);
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification dismissed');
    } catch (error) {
      console.error('Error dismissing notification:', error);
      toast.error('Failed to dismiss notification');
    } finally {
      setProcessing(null);
    }
  };

  const dismissAll = async () => {
    if (dismissingAll || notifications.length === 0) return;

    setDismissingAll(true);
    try {
      const notificationIds = notifications.map(n => n.id);
      const { error } = await supabase
        .from('notifications')
        .delete()
        .in('id', notificationIds);

      if (error) throw error;

      setNotifications([]);
      toast.success('All notifications dismissed');
    } catch (error) {
      console.error('Error dismissing notifications:', error);
      toast.error('Failed to dismiss notifications');
    } finally {
      setDismissingAll(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="relative p-2">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative p-2">
        <button
          onClick={() => fetchNotifications()}
          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
        >
          <Bell className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <Bell className="h-5 w-5" />
        {notifications.length > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {notifications.length}
          </span>
        )}
      </button>

      {showNotifications && notifications.length > 0 && (
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Notifications</h3>
              <button
                onClick={dismissAll}
                disabled={dismissingAll}
                className={`text-sm px-3 py-1 rounded-md ${
                  dismissingAll
                    ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900 dark:text-red-400 dark:hover:bg-red-800'
                }`}
              >
                {dismissingAll ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 dark:border-red-400 mr-2" />
                    Dismissing...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <X className="h-4 w-4 mr-1" />
                    Dismiss all
                  </div>
                )}
              </button>
            </div>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Account: {notification.account_email}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Status changed from {notification.old_status} to {notification.new_status}
                      </p>
                      {notification.comments && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                          Comments: {notification.comments}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => markAsProcessed(notification.id)}
                      disabled={processing === notification.id || dismissingAll}
                      className={`text-sm ${
                        processing === notification.id || dismissingAll
                          ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300'
                      }`}
                    >
                      {processing === notification.id ? 'Dismissing...' : 'Dismiss'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBadge;